import { createSelector } from '@ngrx/store';

import { brandingFeature, createConfigSelector } from '../state/branding.store';

import { selectJob } from './job-tool.reducer';

// Requirement: `job.depositRequired` and the zone's required deposit are both
// floors on what's asked up front, so only their maximum satisfies both — but
// what blocks booking is the zone's requirement alone, since the server's
// `checkEventDepositRequired` rule never reads `depositRequired`.
//
// Requirement: a job with no charges yet is not a $0-deposit job — the total
// only caps the deposit once there is a total to cap it against. The amount
// to book is the exception: it is capped as the server caps it, so a $0 job
// is never blocked here while the server would book it.
export const depositPosition = (
    job: ReturnType<typeof selectJob> | undefined,
    defaultDepositMinor: number,
    depositRequiredToBook: boolean,
) => {
    const totalMinor = Math.max(0, job?.total ?? 0);
    const paidMinor = job?.paidTotal ?? 0;

    const jobMinor = job?.depositRequired ?? 0;
    const zoneMinor = depositRequiredToBook && defaultDepositMinor > 0 ? defaultDepositMinor : 0;

    const capped = (minor: number) => (totalMinor > 0 ? Math.min(minor, totalMinor) : minor);

    const requiredMinor = capped(Math.max(jobMinor, zoneMinor));
    const toBookMinor = Math.min(zoneMinor, totalMinor);

    return {
        requiredMinor,
        source: requiredMinor <= 0
            ? 'none' as const
            : jobMinor >= zoneMinor ? 'job' as const : 'zone' as const,
        outstandingMinor: Math.max(0, requiredMinor - paidMinor),
        toBookMinor,
        outstandingToBookMinor: Math.max(0, toBookMinor - paidMinor),
    };
};

// Requirement: $50 is what the business expects a deposit to look like where
// neither the job nor the zone names one. A zone may set its default to $0,
// so only an unset value falls back.
const FALLBACK_DEPOSIT_MINOR = 5000;

export const selectDefaultDepositMinor = createSelector(
    brandingFeature.selectConfigs,
    (configs) => configs?.CALENDAREVENTS_DEFAULTDEPOSIT ?? FALLBACK_DEPOSIT_MINOR,
);

export const selectDepositRequiredToBook = createConfigSelector('CALENDAREVENTS_DEPOSITREQUIREDTOBOOK', false);

export const selectJobDepositPosition = createSelector(
    selectJob,
    selectDefaultDepositMinor,
    selectDepositRequiredToBook,
    depositPosition,
);
